import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { intlLocaleFor } from "../../lib/i18n";
import { MONDAY_WEEK, addMonths, clampDay, initialFocus, monthGrid, monthOf, moveFocus, ymdToUtcDate } from "./calendar-model";

export interface DatePickerCalendarProps {
	/** The picked day (`YYYY-MM-DD`), or null. */
	value: string | null;
	onSelect: (ymd: string) => void;
	/** The only days that can be picked, ascending. Null = every day. The picked day is always shown as picked. */
	enabled: readonly string[] | null;
	/** Names the grid for assistive tech ("Days MH 602 flies"). */
	label: string;
	/** "Today" for the today ring and the starting month, `YYYY-MM-DD` in the zone the dates are in. */
	today: string;
	/** Escape inside the grid: the caller closes it and returns focus. */
	onEscape?: () => void;
	/** Focus the starting day on mount (opened from a button). */
	autoFocus?: boolean;
	/** With nothing picked: open on the first pickable day from here instead of from `today` (a transit flight after the previous one). */
	startFrom?: string | null;
	id?: string;
}

/**
 * A month grid that can limit which days are pickable — `<input type=date>`
 * cannot. APG date-picker grid: one tab stop (roving), arrows ±1 day / week,
 * Home / End the week, PageUp / PageDown a month (Shift: a year), Enter or
 * Space picks, Escape hands back to the caller. Days that cannot be picked
 * stay focusable with `aria-disabled`, so moving through the month never
 * skips silently. Week starts Monday; month and weekday names come from Intl
 * in the UI language (`intlLocaleFor`). Navigation stops at the first and
 * last month that has a pickable day.
 */
export function DatePickerCalendar({ value, onSelect, enabled, label, today, onEscape, autoFocus = false, id, startFrom = null }: DatePickerCalendarProps) {
	const { t, i18n } = useTranslation("common");
	const loc = intlLocaleFor(i18n.language);
	const intlLoc = loc === "en" ? "en-GB" : loc;

	const enabledSet = useMemo(() => (enabled ? new Set(enabled) : null), [enabled]);
	const [min, max] = useMemo(() => {
		if (!enabled || enabled.length === 0) return [null, null] as const;
		const lo = value && value < enabled[0]! ? value : enabled[0]!;
		const hi = value && value > enabled.at(-1)! ? value : enabled.at(-1)!;
		return [lo, hi] as const;
	}, [enabled, value]);

	const [focus, setFocus] = useState(() => clampDay(initialFocus(value, enabled, startFrom ?? today), min, max));
	const [month, setMonth] = useState(() => monthOf(focus));
	const wantFocus = useRef(autoFocus);
	const grid = useRef<HTMLTableElement>(null);

	// A new picked day from outside (another flight) moves the view to it.
	useEffect(() => {
		if (!value) return;
		setFocus(value);
		setMonth(monthOf(value));
	}, [value]);

	useEffect(() => {
		if (!wantFocus.current) return;
		wantFocus.current = false;
		grid.current?.querySelector<HTMLButtonElement>(`button[data-day="${focus}"]`)?.focus();
	});

	const fmt = useMemo(
		() => ({
			title: new Intl.DateTimeFormat(intlLoc, { month: "long", year: "numeric", timeZone: "UTC" }),
			weekdayShort: new Intl.DateTimeFormat(intlLoc, { weekday: "narrow", timeZone: "UTC" }),
			weekdayLong: new Intl.DateTimeFormat(intlLoc, { weekday: "long", timeZone: "UTC" }),
			day: new Intl.DateTimeFormat(intlLoc, { day: "numeric", timeZone: "UTC" }),
			full: new Intl.DateTimeFormat(intlLoc, { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }),
		}),
		[intlLoc],
	);

	const pickable = (d: string) => !enabledSet || enabledSet.has(d) || d === value;
	const minMonth = min ? monthOf(min) : null;
	const maxMonth = max ? monthOf(max) : null;
	const canPrev = !minMonth || month > minMonth;
	const canNext = !maxMonth || month < maxMonth;

	const goTo = (day: string, takeFocus: boolean) => {
		const d = clampDay(day, min, max);
		setFocus(d);
		setMonth(monthOf(d));
		wantFocus.current = takeFocus;
	};

	const shiftMonth = (n: number) => {
		const next = monthOf(addMonths(`${month}-01`, n));
		setMonth(next);
		// Keep the roving tab stop inside the month on view.
		const inMonth = enabled?.find((d) => monthOf(d) === next) ?? clampDay(`${next}-01`, min, max);
		setFocus(monthOf(focus) === next ? focus : inMonth);
	};

	const onKey = (e: KeyboardEvent<HTMLButtonElement>, day: string) => {
		if (e.key === "Escape" && onEscape) {
			e.preventDefault();
			e.stopPropagation(); // a sheet behind must not close too
			onEscape();
			return;
		}
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault(); // never submits the form around it
			if (pickable(day)) onSelect(day);
			return;
		}
		const next = moveFocus(day, e.key, e.shiftKey);
		if (!next) return;
		e.preventDefault();
		goTo(next, true);
	};

	const titleId = `${id ?? "cal"}-title`;
	const navBtn = "inline-flex size-[1.875rem] items-center justify-center rounded-[0.375rem] text-gms-ink-2 hover:bg-gms-soft-2 hover:text-gms-navy disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent focus-visible:outline-2 focus-visible:outline-gms-navy";

	return (
		<div id={id} className="w-full max-w-[19rem] select-none">
			<div className="mb-[0.375rem] flex items-center justify-between gap-[0.5rem]">
				<button type="button" className={navBtn} onClick={() => shiftMonth(-1)} disabled={!canPrev} aria-label={t("calendar.previousMonth")}>
					<Chevron dir="left" />
				</button>
				<span id={titleId} aria-live="polite" className="text-[0.84375rem] font-semibold text-gms-navy-ink">
					{fmt.title.format(ymdToUtcDate(`${month}-01`))}
				</span>
				<button type="button" className={navBtn} onClick={() => shiftMonth(1)} disabled={!canNext} aria-label={t("calendar.nextMonth")}>
					<Chevron dir="right" />
				</button>
			</div>
			<table ref={grid} role="grid" aria-label={label} aria-describedby={titleId} className="w-full table-fixed border-separate border-spacing-[0.125rem]">
				<thead>
					<tr>
						{MONDAY_WEEK.map((d) => (
							<th key={d.toISOString()} scope="col" abbr={fmt.weekdayLong.format(d)} className="pb-[0.125rem] text-center text-[0.6875rem] font-medium text-gms-ink-3">
								{fmt.weekdayShort.format(d)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{monthGrid(month).map((week) => (
						<tr key={week.find(Boolean)}>
							{week.map((day, i) => {
								if (!day) return <td key={`blank-${i}`} role="gridcell" aria-hidden />;
								const can = pickable(day);
								const picked = day === value;
								const isToday = day === today;
								const tone = picked
									? "bg-gms-navy text-white font-semibold"
									: can
										? "bg-gms-navy-soft text-gms-navy font-semibold hover:bg-gms-navy hover:text-white"
										: "text-gms-ink-3/70 cursor-default";
								return (
									<td key={day} role="gridcell" aria-selected={picked} className="p-0">
										<button
											type="button"
											data-day={day}
											tabIndex={day === focus ? 0 : -1}
											aria-disabled={!can || undefined}
											aria-current={isToday ? "date" : undefined}
											aria-label={fmt.full.format(ymdToUtcDate(day))}
											onClick={() => {
												setFocus(day);
												if (can) onSelect(day);
											}}
											onKeyDown={(e) => onKey(e, day)}
											className={`relative mx-auto flex h-[2rem] w-full max-w-[2.25rem] items-center justify-center rounded-[0.375rem] text-[0.8125rem] tabular-nums transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gms-navy ${tone}`}
										>
											{fmt.day.format(ymdToUtcDate(day))}
											{isToday && <span aria-hidden className={`absolute bottom-[0.1875rem] size-[0.1875rem] rounded-full ${picked ? "bg-white" : "bg-current"}`} />}
										</button>
									</td>
								);
							})}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function Chevron({ dir }: { dir: "left" | "right" }) {
	return (
		<svg viewBox="0 0 24 24" aria-hidden className="size-[1rem]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
			<path d={dir === "left" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} />
		</svg>
	);
}
